import styled, { css } from 'styled-components';
import { StyledComponentPropsWithAs } from 'babel-plugin-styled-components';

import { MOBILE_WIDTH } from '../../../helpers/constants/responsive-breakpoints';
import { customTextTokens } from '../../../tokens/components/CustomText/tokens';
import { TooltipContent } from '../../Tooltip/styles';
import { colorTokens } from '../../../tokens/globals/tokens';

const weights = {
  inherit: 'inherit',
  '300': '300',
  '400': '400',
  '500': '500',
  '600': '600',
  '700': '700',
  light: '300',
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
};

const sizes = {
  '90': '90px',
  '72': '72px',
  '60': '60px',
  '48': '48px',
  '36': '36px',
  '30': '30px',
  '28': '28px',
  '24': '24px',
  '20': '20px',
  '18': '18px',
  '16': '16px',
  '14': '14px',
  '13': '13px',
  '12': '12px',
  '11': '11px',
  xlarge: '18px',
  large: '16px',
  body: '14px',
  small: '13px',
  xsmall: '12px',
};

const colors = {
  inherit: 'inherit',
  black: customTextTokens.FontColorBlackDefault,
  gray: customTextTokens.FontColorGrayDefault,
  white: customTextTokens.FontColorWhiteDefault,
  blue: customTextTokens.FontColorBlueDefault,
  red: customTextTokens.FontColorRedDefault,
  green: customTextTokens.FontColorGreenDefault,
  body: customTextTokens.FontColorBodyDefault,
};

const colorStyles = {
  'feedback-positive': css`
    color: ${customTextTokens.FontColorPositiveDefault};
  `,
  'feedback-negative': css`
    color: ${customTextTokens.FontColorNegativeDefault};
  `,
  'feedback-neutral': css`
    color: ${customTextTokens.FontColorNeutralDefault};
  `,
  monospace: css`
    background: ${customTextTokens.BackgroundColorMonospaceDefault};
    color: ${customTextTokens.FontColorMonospaceDefault};
  `,
};

const getTooltipColors = variant => {
  if (variant.includes('feedback')) {
    if (variant.includes('positive')) {
      return css`
        color: ${colorTokens.GlobalFontFeedbackPositiveIntensity50Default};
      `;
    }

    if (variant.includes('negative')) {
      return css`
        color: ${colorTokens.GlobalFontFeedbackNegativeIntensity30Default};
      `;
    }

    if (variant.includes('neutral')) {
      return css`
        color: ${colorTokens.GlobalFontFeedbackNeutralIntensity50Default};
      `;
    }
  }
  return css``;
};

const hierarchyStyles = {
  'display-1': css`
    font-size: ${customTextTokens.FontSizeDisplay1Default};
    font-family: ${customTextTokens.FontFamilyDisplay1Default};
    font-weight: ${customTextTokens.FontWeightDisplay1Default};
    line-height: ${customTextTokens.FontLeadingDisplay1Default};
  `,
  'display-2': css`
    font-size: ${customTextTokens.FontSizeDisplay2Default};
    font-family: ${customTextTokens.FontFamilyDisplay2Default};
    font-weight: ${customTextTokens.FontWeightDisplay2Default};
    line-height: ${customTextTokens.FontLeadingDisplay2Default};
  `,
  'heading-1': css`
    font-size: ${customTextTokens.FontSizeHeading1DefaultDesktop};
    font-family: ${customTextTokens.FontFamilyHeading1Default};
    font-weight: ${customTextTokens.FontWeightHeading1Default};
    line-height: ${customTextTokens.FontLeadingHeading1DefaultDesktop};

    @media (max-width: ${MOBILE_WIDTH}px) {
      font-size: ${customTextTokens.FontSizeHeading1DefaultMobile};
      line-height: ${customTextTokens.FontLeadingHeading1DefaultMobile};
    }
  `,
  'heading-2': css`
    font-size: ${customTextTokens.FontSizeHeading2DefaultDesktop};
    font-family: ${customTextTokens.FontFamilyHeading2Default};
    font-weight: ${customTextTokens.FontWeightHeading2Default};
    line-height: ${customTextTokens.FontLeadingHeading2DefaultDesktop};

    @media (max-width: ${MOBILE_WIDTH}px) {
      font-size: ${customTextTokens.FontSizeHeading2DefaultMobile};
      line-height: ${customTextTokens.FontLeadingHeading2DefaultMobile};
    }
  `,
  'heading-3': css`
    font-size: ${customTextTokens.FontSizeHeading3Default};
    font-family: ${customTextTokens.FontFamilyHeading3Default};
    font-weight: ${customTextTokens.FontWeightHeading3Default};
    line-height: ${customTextTokens.FontLeadingHeading3Default};
  `,
  'heading-4': css`
    font-size: ${customTextTokens.FontSizeHeading4Default};
    font-family: ${customTextTokens.FontFamilyHeading4Default};
    font-weight: ${customTextTokens.FontWeightHeading4Default};
    line-height: ${customTextTokens.FontLeadingHeading4Default};
  `,
  'body-1': css`
    font-size: ${customTextTokens.FontSizeBody1Default};
    font-family: ${customTextTokens.FontFamilyBody1Default};
    font-weight: ${customTextTokens.FontWeightBody1Default};
    line-height: ${customTextTokens.FontLeadingBody1Default};
  `,
  'body-2': css`
    font-size: ${customTextTokens.FontSizeBody2Default};
    font-family: ${customTextTokens.FontFamilyBody2Default};
    font-weight: ${customTextTokens.FontWeightBody2Default};
    line-height: ${customTextTokens.FontLeadingBody2Default};
  `,
  'body-3': css`
    font-size: ${customTextTokens.FontSizeBody3Default};
    font-family: ${customTextTokens.FontFamilyBody3Default};
    font-weight: ${customTextTokens.FontWeightBody3Default};
    line-height: ${customTextTokens.FontLeadingBody3Default};
  `,
  'small-1': css`
    font-size: ${customTextTokens.FontSizeSmall1Default};
    font-family: ${customTextTokens.FontFamilySmall1Default};
    font-weight: ${customTextTokens.FontWeightSmall1Default};
    line-height: ${customTextTokens.FontLeadingSmall1Default};
  `,
  'small-2': css`
    font-size: ${customTextTokens.FontSizeSmall2Default};
    font-family: ${customTextTokens.FontFamilySmall2Default};
    font-weight: ${customTextTokens.FontWeightSmall2Default};
    line-height: ${customTextTokens.FontLeadingSmall2Default};
  `,
  monospace: css`
    font-size: ${customTextTokens.FontSizeMonospaceDefault};
    font-family: ${customTextTokens.FontFamilyMonospaceDefault};
    font-weight: ${customTextTokens.FontWeightMonospaceDefault};
    line-height: ${customTextTokens.FontLeadingMonospaceDefault};
  `,
};

function getVariantStyles(variant: string, trim: boolean) {
  switch (variant) {
    case 'feedback-positive':
      return css`
        ${colorStyles['feedback-positive']};
      `;
    case 'feedback-negative':
      return css`
        ${colorStyles['feedback-negative']};
      `;
    case 'feedback-neutral':
      return css`
        ${colorStyles['feedback-neutral']};
      `;
    case 'display-1':
      return css`
        ${hierarchyStyles['display-1']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingDisplay1Default};
        `};
      `;
    case 'display-1-feedback-positive':
      return css`
        ${colorStyles['feedback-positive']};
        ${hierarchyStyles['display-1']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingDisplay1Default};
        `};
      `;
    case 'display-1-feedback-negative':
      return css`
        ${colorStyles['feedback-negative']};
        ${hierarchyStyles['display-1']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingDisplay1Default};
        `};
      `;
    case 'display-2':
      return css`
        ${hierarchyStyles['display-2']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingDisplay2Default};
        `};
      `;
    case 'display-2-feedback-positive':
      return css`
        ${colorStyles['feedback-positive']};
        ${hierarchyStyles['display-2']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingDisplay1Default};
        `};
      `;
    case 'display-2-feedback-negative':
      return css`
        ${colorStyles['feedback-negative']};
        ${hierarchyStyles['display-2']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingDisplay1Default};
        `};
      `;
    case 'heading-1':
      return css`
        ${hierarchyStyles['heading-1']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingHeading1Default};
        `};
      `;
    case 'heading-2':
      return css`
        ${hierarchyStyles['heading-2']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingHeading2Default};
        `};
      `;
    case 'heading-3':
      return css`
        ${hierarchyStyles['heading-3']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingHeading3Default};
        `};
      `;
    case 'heading-4':
      return css`
        ${hierarchyStyles['heading-4']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingHeading4Default};
        `};
      `;
    case 'body-1':
      return css`
        ${hierarchyStyles['body-1']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingBody1Default};
        `};
      `;
    case 'body-2':
      return css`
        ${hierarchyStyles['body-2']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingBody2Default};
        `};
      `;
    case 'body-3':
      return css`
        ${hierarchyStyles['body-3']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingBody3Default};
        `};
      `;
    case 'body-3-feedback-neutral':
      return css`
        ${colorStyles['feedback-neutral']};
        ${hierarchyStyles['body-3']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingBody3Default};
        `};
      `;

    case 'body-3-feedback-negative':
      return css`
        ${colorStyles['feedback-negative']};
        ${hierarchyStyles['body-3']};
        margin-bottom: ${trim === true ? '0' : customTextTokens.FontSpacingBody3Default};
      `;

    case 'body-3-feedback-positive':
      return css`
        ${colorStyles['feedback-positive']};
        ${hierarchyStyles['body-3']};
        margin-bottom: ${trim === true ? '0' : customTextTokens.FontSpacingBody3Default};
      `;
    case 'small-1':
      return css`
        ${hierarchyStyles['small-1']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingSmall1Default};
        `};
      `;
    case 'small-2':
      return css`
        ${hierarchyStyles['small-2']};
        ${!trim &&
        css`
          margin-bottom: ${customTextTokens.FontSpacingSmall2Default};
        `};
      `;
    case 'monospace':
      return css`
        ${hierarchyStyles['monospace']};
        ${colorStyles['monospace']};
        border-radius: 4px;
        display: inline-block;
        padding: 0 6px;
        white-space: pre-wrap;
        word-break: break-all;
      `;
    default:
      return css``;
  }
}

export const CustomTextElement = styled.p<StyledComponentPropsWithAs>`
  color: ${({ theme }) => colors[theme.customTextColor]};
  display: ${({ theme }) => theme.customTextDisplay};
  font-weight: ${({ theme }) => weights[theme.customTextWeight]};
  line-height: 1.5;
  margin: 0;
  text-align: ${({ theme }) => theme.customTextAlign};
  text-transform: ${({ theme }) => theme.customTextTransform};
  white-space: ${({ theme }) => theme.customTextWhiteSpace};
  word-break: ${({ theme }) => theme.customTextWordBreak};

  ${TooltipContent} && {
    // lightens the text color for better contrast
    ${({ theme }) => theme.customTextVariant && getTooltipColors(theme.customTextVariant)}
  }

  ${({ theme }) =>
    theme.customTextDisplay &&
    css`
      display: ${theme.customTextDisplay};
    `}

  ${({ theme }) =>
    css`
      font-size: ${sizes[theme.customTextSize]};
    `}

  ${({ theme }) => theme.customTextVariant && getVariantStyles(theme.customTextVariant, theme.customTextTrim)};
`;
